import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, TrendUp, TrendDown, Package, ShoppingCart, Users, UserMinus, MagnifyingGlass, CaretLeft, CaretRight, Eye, Receipt, PlayCircle, CaretUp, CaretDown, CaretUpDown } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const PAGE_SIZE = 25;

// Cada tab tem `defaultSort` (campo + direção iniciais) e `numeric` define quais
// dos campos devem ser ordenados numericamente (em vez de string/locale).
const SORT_CONFIG = {
  unfinalized: { defaultSort: { key: 'DataEncomenda', dir: 'desc' }, numeric: ['ID_Encomenda', 'PrecoFinal'], date: ['DataEncomenda'] },
  viewedNotBought: { defaultSort: { key: 'NrVisualizacoes', dir: 'desc' }, numeric: ['NrVisualizacoes'], date: ['PrimeiraVisita', 'UltimaVisita'] },
  top: { defaultSort: { key: 'TotalValor', dir: 'desc' }, numeric: ['TotalQuantidade', 'TotalValor'] },
  bottom: { defaultSort: { key: 'TotalValor', dir: 'asc' }, numeric: ['TotalQuantidade', 'TotalValor'] },
  purchased: { defaultSort: { key: 'TotalValor', dir: 'desc' }, numeric: ['TotalQuantidade', 'TotalValor'] },
  not: { defaultSort: { key: 'Descricao', dir: 'asc' }, numeric: [] },
  topBuyers: { defaultSort: { key: 'totalCompras', dir: 'desc' }, numeric: ['totalCompras'], date: ['DataRegisto'] },
  bottomBuyers: { defaultSort: { key: 'totalCompras', dir: 'asc' }, numeric: ['totalCompras'], date: ['DataRegisto'] },
  noOrders: { defaultSort: { key: 'NomeCompleto', dir: 'asc' }, numeric: [], date: ['DataRegisto'] },
  withOrders: { defaultSort: { key: 'totalCompras', dir: 'desc' }, numeric: ['totalCompras'], date: ['DataRegisto'] },
  mediaCenter: { defaultSort: { key: 'NrVizualizacao', dir: 'desc' }, numeric: ['NrVizualizacao'], date: ['DataDisponibilizacao', 'DataUltimaVizualizacao'] },
};

function compareValues(a, b, key, cfg, dir) {
  const av = a?.[key];
  const bv = b?.[key];
  // Null/undefined sempre no fim, independentemente da direção.
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  let cmp;
  if (cfg.numeric?.includes(key)) {
    cmp = Number(av) - Number(bv);
  } else if (cfg.date?.includes(key)) {
    // Backend devolve datas em formato `dd-mm-yyyy` (T-SQL style 105). Comparar
    // lexicograficamente assim daria ordem errada (e.g. "31-12-2024" > "01-01-2025").
    // Convertemos para `yyyy-mm-dd` para garantir ordenação cronológica correcta.
    // Suporta também ISO `yyyy-mm-dd[ HH:mm:ss]` (devolvido como-está).
    cmp = toSortableDate(av).localeCompare(toSortableDate(bv));
  } else {
    cmp = String(av).localeCompare(String(bv), 'pt', { sensitivity: 'base' });
  }
  return dir === 'asc' ? cmp : -cmp;
}

// Converte uma data textual para forma ordenável `yyyy-mm-dd[ HH:mm:ss]`.
// Aceita `dd-mm-yyyy`, `dd/mm/yyyy`, ISO `yyyy-mm-dd...`. Outros formatos
// passam intactos (fallback lexicográfico).
function toSortableDate(v) {
  const s = String(v).trim();
  // dd-mm-yyyy ou dd/mm/yyyy (com opcional " HH:MM:SS")
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})(.*)$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}${m[4]}`;
  return s;
}

export default function OpportunitiesPage() {
  const { activeId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [topSelling, setTopSelling] = useState([]);
  const [bottomSelling, setBottomSelling] = useState([]);
  const [productsNotBought, setProductsNotBought] = useState([]);
  const [productsPurchased, setProductsPurchased] = useState([]);
  const [topBuyers, setTopBuyers] = useState([]);
  const [bottomBuyers, setBottomBuyers] = useState([]);
  const [clientsNoOrders, setClientsNoOrders] = useState([]);
  const [clientsWithOrders, setClientsWithOrders] = useState([]);
  const [countNoOrders, setCountNoOrders] = useState(0);
  const [countOld, setCountOld] = useState(0);
  const [viewedNotBought, setViewedNotBought] = useState([]);
  const [unfinalizedOrders, setUnfinalizedOrders] = useState([]);
  const [mediaCenterViews, setMediaCenterViews] = useState([]);
  const [viewersModal, setViewersModal] = useState(null);
  const [viewersData, setViewersData] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [tab, setTab] = useState('unfinalized');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(SORT_CONFIG.unfinalized.defaultSort);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.post('/oportunidades/topSellingProducts').then(r => setTopSelling(r.data?.data || [])).catch(() => {}),
      api.post('/oportunidades/bottomSellingProducts').then(r => setBottomSelling(r.data?.data || [])).catch(() => {}),
      api.post('/indicadores/productsNotBought').then(r => {
        const d = r.data?.data || [];
        d.sort((a, b) => (a.Descricao || '').localeCompare(b.Descricao || '', 'pt'));
        setProductsNotBought(d);
      }).catch(() => {}),
      api.post('/encomendas/productsPurchased').then(r => setProductsPurchased(r.data?.data || [])).catch(() => {}),
      api.post('/indicadores/topBuyers').then(r => setTopBuyers(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/bottomBuyers').then(r => setBottomBuyers(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/clientesNoOrders').then(r => setClientsNoOrders(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/clientsOrders').then(r => setClientsWithOrders(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/clientsWithNoOrders').then(r => { const c = r.data?.clientes; setCountNoOrders(c?.[0]?.numero || 0); }).catch(() => {}),
      api.post('/indicadores/clientsWithOrders1YearAgo').then(r => { const c = r.data?.clientes; setCountOld(c?.[0]?.numero || 0); }).catch(() => {}),
      api.post('/oportunidades/viewedNotBought').then(r => setViewedNotBought(r.data?.data || [])).catch(() => {}),
      api.post('/oportunidades/unfinalizedOrders').then(r => setUnfinalizedOrders(r.data?.data || [])).catch(() => {}),
      api.post('/oportunidades/mediaCenterViews').then(r => setMediaCenterViews(r.data?.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [activeId]);

  // Quando se muda de tab, reset page + sort para o default da nova tab.
  useEffect(() => {
    setPage(1);
    setSort(SORT_CONFIG[tab]?.defaultSort || { key: null, dir: 'asc' });
  }, [tab]);
  useEffect(() => { setPage(1); }, [search]);

  const onSort = (key) => {
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return { key, dir: 'asc' };
    });
  };

  const tabs = [
    { key: 'unfinalized', label: 'Enc. Não Finalizadas', icon: Receipt },
    { key: 'viewedNotBought', label: 'Vistos Não Comprados', icon: Eye },
    { key: 'top', label: 'Mais Vendidos', icon: TrendUp },
    { key: 'bottom', label: 'Menos Vendidos', icon: TrendDown },
    { key: 'purchased', label: 'Produtos Comprados', icon: ShoppingCart },
    { key: 'not', label: 'Não Comprados', icon: Package },
    { key: 'topBuyers', label: 'Top Compradores', icon: Users },
    { key: 'bottomBuyers', label: 'Menos Compradores', icon: UserMinus },
    { key: 'noOrders', label: 'Sem Encomendas', icon: UserMinus },
    { key: 'withOrders', label: 'Com Encomendas', icon: Users },
    { key: 'mediaCenter', label: 'MediaCenter', icon: PlayCircle },
  ];

  const rawData = useMemo(() => {
    switch (tab) {
      case 'top': return topSelling;
      case 'bottom': return bottomSelling;
      case 'purchased': return productsPurchased;
      case 'not': return productsNotBought;
      case 'topBuyers': return topBuyers;
      case 'bottomBuyers': return bottomBuyers;
      case 'noOrders': return clientsNoOrders;
      case 'withOrders': return clientsWithOrders;
      case 'viewedNotBought': return viewedNotBought;
      case 'unfinalized': return unfinalizedOrders;
      case 'mediaCenter': return mediaCenterViews;
      default: return [];
    }
  }, [tab, topSelling, bottomSelling, productsPurchased, productsNotBought, topBuyers, bottomBuyers, clientsNoOrders, clientsWithOrders, viewedNotBought, unfinalizedOrders, mediaCenterViews]);

  const filtered = useMemo(() => {
    const searchKey = search.trim().toLowerCase();
    let rows = rawData;
    if (searchKey) {
      rows = rows.filter(r => {
        const name = (r.Descricao || r.Produto || r.NomeCompleto || '').toLowerCase();
        const email = (r.Email || r.NomeCompleto || '').toLowerCase();
        const estado = (r.Estado || '').toLowerCase();
        return name.includes(searchKey) || email.includes(searchKey) || estado.includes(searchKey);
      });
    }
    if (sort.key) {
      const cfg = SORT_CONFIG[tab] || { numeric: [], date: [] };
      rows = [...rows].sort((a, b) => compareValues(a, b, sort.key, cfg, sort.dir));
    }
    return rows;
  }, [rawData, search, sort, tab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const data = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const isProductTab = ['top', 'bottom', 'purchased', 'not'].includes(tab);
  const isClientTab = ['topBuyers', 'bottomBuyers', 'noOrders', 'withOrders'].includes(tab);
  const isViewedTab = tab === 'viewedNotBought';
  const isUnfinalizedTab = tab === 'unfinalized';
  const isMediaTab = tab === 'mediaCenter';
  const showValues = tab !== 'not' && tab !== 'noOrders';

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  return (
    <div data-testid="opportunities-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Oportunidades</h1>
        <p className="text-sm text-[#6B7280] mt-1">Produtos, compradores e oportunidades de venda na rede</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card label="Mais Vendidos" value={topSelling.length} color="#00C853" />
        <Card label="Menos Vendidos" value={bottomSelling.length} color="#FF6B2B" />
        <Card label="Top Compradores" value={topBuyers.length} color="#0F1829" />
        <Card label="Sem Encomendas" value={countNoOrders} color="#FF2B2B" sub={`${countOld} sem encomendas >1 ano`} />
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${tab === t.key ? 'border-[#0F1829] text-[#0F1829]' : 'border-transparent text-[#6B7280] hover:text-[#111827]'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full sm:w-80 pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none"
          placeholder={isProductTab || isViewedTab ? 'Pesquisar produto...' : 'Pesquisar cliente...'} />
        {search && <span className="ml-3 text-xs text-[#6B7280]">{filtered.length} resultado(s)</span>}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              {isProductTab && <SortableTH field="Descricao" sort={sort} onSort={onSort}>Produto</SortableTH>}
              {isClientTab && <SortableTH field="NomeCompleto" sort={sort} onSort={onSort}>Cliente</SortableTH>}
              {isViewedTab && <>
                <SortableTH field="Produto" sort={sort} onSort={onSort}>Produto</SortableTH>
                <SortableTH field="NomeCompleto" sort={sort} onSort={onSort}>Cliente</SortableTH>
                <SortableTH field="NrVisualizacoes" sort={sort} onSort={onSort} align="right">Visualizações</SortableTH>
                <SortableTH field="PrimeiraVisita" sort={sort} onSort={onSort}>Primeira Vista</SortableTH>
                <SortableTH field="UltimaVisita" sort={sort} onSort={onSort}>Última Vista</SortableTH>
              </>}
              {isUnfinalizedTab && <>
                <SortableTH field="ID_Encomenda" sort={sort} onSort={onSort}>Encomenda</SortableTH>
                <SortableTH field="NomeCompleto" sort={sort} onSort={onSort}>Cliente</SortableTH>
                <SortableTH field="Estado" sort={sort} onSort={onSort}>Estado</SortableTH>
                <SortableTH field="DataEncomenda" sort={sort} onSort={onSort}>Data</SortableTH>
                <SortableTH field="PrecoFinal" sort={sort} onSort={onSort} align="right">Valor</SortableTH>
              </>}
              {isMediaTab && <>
                <SortableTH field="Descricao" sort={sort} onSort={onSort}>Produto</SortableTH>
                <SortableTH field="NomeCompleto" sort={sort} onSort={onSort}>Cliente</SortableTH>
                <SortableTH field="NrVizualizacao" sort={sort} onSort={onSort} align="right">Visualizações</SortableTH>
                <SortableTH field="DataDisponibilizacao" sort={sort} onSort={onSort}>Disponível</SortableTH>
                <SortableTH field="DataUltimaVizualizacao" sort={sort} onSort={onSort}>Última Vista</SortableTH>
              </>}
              {isClientTab && tab !== 'noOrders' && <SortableTH field="totalCompras" sort={sort} onSort={onSort} align="right">Total Compras</SortableTH>}
              {isClientTab && <SortableTH field="DataRegisto" sort={sort} onSort={onSort}>Data Registo</SortableTH>}
              {isProductTab && showValues && <SortableTH field="TotalQuantidade" sort={sort} onSort={onSort} align="right">Qtd</SortableTH>}
              {isProductTab && showValues && <SortableTH field="TotalValor" sort={sort} onSort={onSort} align="right">Valor</SortableTH>}
            </tr></thead>
            <tbody>
              {data.map((p, i) => (
                <tr key={`opp-${p.ID_Cliente || ''}-${p.ID_Produto || p.Produto || ''}-${p.Descricao || ''}-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                  {isProductTab && <td className="px-4 py-3 text-sm font-medium text-[#111827]">{p.Descricao}</td>}
                  {isClientTab && (
                    <td className="px-4 py-3 text-sm font-medium text-[#111827]">
                      <PersonLink id={p.ID_Cliente} name={p.NomeCompleto} />
                    </td>
                  )}
                  {isViewedTab && <>
                    <td className="px-4 py-3 text-sm font-medium text-[#111827]">{p.Produto}</td>
                    <td className="px-4 py-3 text-sm"><PersonLink id={p.ID_Cliente} name={p.NomeCompleto} /></td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#0F1829] font-semibold">{p.NrVisualizacoes}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.PrimeiraVisita || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.UltimaVisita || '-'}</td>
                  </>}
                  {isUnfinalizedTab && <>
                    <td className="px-4 py-3 text-sm font-mono text-[#0F1829] cursor-pointer hover:underline" onClick={() => window.location.href = `/encomendas/${p.ID_Encomenda}`}>#{p.ID_Encomenda}</td>
                    <td className="px-4 py-3 text-sm"><PersonLink id={p.ID_Cliente} name={p.NomeCompleto} /></td>
                    <td className="px-4 py-3"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">{p.Estado}</span></td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.DataEncomenda}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(p.PrecoFinal)}</td>
                  </>}
                  {isMediaTab && <>
                    <td className="px-4 py-3 text-sm font-medium text-[#111827]">{p.Descricao}</td>
                    <td className="px-4 py-3 text-sm"><PersonLink id={p.ID_Cliente} name={p.NomeCompleto} /></td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#0F1829] font-semibold">{p.NrVizualizacao}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.DataDisponibilizacao || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.DataUltimaVizualizacao || '-'}</td>
                  </>}
                  {isClientTab && tab !== 'noOrders' && (
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(p.totalCompras)}</td>
                  )}
                  {isClientTab && (
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{p.DataRegisto ? new Date(p.DataRegisto).toLocaleDateString('pt-PT') : '-'}</td>
                  )}
                  {isProductTab && showValues && <td className="px-4 py-3 text-sm font-mono text-right text-[#6B7280]">{p.TotalQuantidade}</td>}
                  {isProductTab && showValues && <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(p.TotalValor)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem dados</div>}

        {totalPages > 1 && (
          <div data-testid="pagination" className="flex flex-col items-center gap-2 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({filtered.length} registos)</p>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                <CaretLeft size={12} /> Anterior
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                Seguinte <CaretRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, color, sub }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
      <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">{label}</p>
      <p className="text-2xl font-bold font-mono mt-0.5" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</p>}
    </div>
  );
}

function TH({ children, align = 'left' }) {
  return <th className={`text-${align} text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] px-4 py-3 whitespace-nowrap`}>{children}</th>;
}

function SortableTH({ children, align = 'left', field, sort, onSort }) {
  const isActive = sort?.key === field;
  const dir = isActive ? sort.dir : null;
  const Icon = !isActive ? CaretUpDown : (dir === 'asc' ? CaretUp : CaretDown);
  return (
    <th
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
      data-sort-dir={dir || 'none'}
      className={`text-${align} text-[10px] uppercase tracking-[0.15em] font-medium px-4 py-3 whitespace-nowrap cursor-pointer select-none transition-colors ${isActive ? 'text-[#0F1829]' : 'text-[#6B7280] hover:text-[#111827]'}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        <Icon size={11} weight={isActive ? 'bold' : 'regular'} className={isActive ? 'opacity-100' : 'opacity-40'} />
      </span>
    </th>
  );
}
